import {
  getTaskCheckOption,
  getTaskDayCompletionPercentage,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTaskTodayControls } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { IconFormIcons, Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useUser } from 'shared/hooks/use-user';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import * as Styled from './search-task-card.style';

export type SearchTaskCardProps = {
  task: Task;
  showDetailOnSelect?: boolean;
  onClick?: (id: ID) => void;
  tabbable?: boolean;
};

export const SearchTaskCard: React.FC<SearchTaskCardProps> = ({
  task,
  showDetailOnSelect = true,
  onClick,
  tabbable,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const activeSchedule = useActiveSchedule(task.schedules, today);

  const openTaskDetail = useOpenTaskDetail();

  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task, { day: today, weekStartsOn })
        : undefined,
    [task, today, weekStartsOn],
  );

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: today,
        today,
        weekStartsOn,
      }),
    [task, today, weekStartsOn],
  );

  const linkedItem = useMemo(
    () =>
      task.goalId
        ? goals.find(({ id }) => id === task.goalId)
        : task.lifeAreaId
          ? lifeAreas.find(({ id }) => id === task.lifeAreaId)
          : undefined,
    [goals, lifeAreas, task.goalId, task.lifeAreaId],
  );

  const linkedItemColor = useMemo(
    () =>
      linkedItem?.id
        ? 'lifeAreaId' in linkedItem
          ? getLinkedItemColor(linkedItem.id, { lifeAreas, goals }) ?? undefined
          : linkedItem.color
        : undefined,
    [linkedItem, lifeAreas, goals],
  );

  const { onComplete, openManualEntryLogging } = useTaskTodayControls();

  const _onComplete = (date: Timestamp) =>
    onComplete(task, ScheduleEntryState.Complete, {
      toggleDayEntry: true,
      referenceDate: date,
    });

  const _onEntryChange = (
    type: ScheduleEntryState,
    options?: { value?: number; increment?: number },
  ) => onComplete(task, type, { ...options, toggleDayEntry: true });

  const _onManual = (date: Timestamp) => openManualEntryLogging(task.id, date);

  const _onClick = (e: React.MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    showDetailOnSelect && openTaskDetail(task);
    onClick?.(task.id);
  };

  useKeysDown(
    ['Enter'],
    (e) => {
      if (tabbable && document.activeElement === containerRef.current) {
        _onClick(e);
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container
      ref={containerRef}
      onClick={_onClick}
      $isClickable={!!onClick || showDetailOnSelect}
      tabIndex={tabbable ? 0 : undefined}
    >
      <TaskDetailCheckbox
        checked={checked}
        name={task.name}
        icon={task.iconName ? IconFormIcons[task.iconName] : undefined}
        completionPercentage={completionPercentage}
        target={target}
        priority={task.priority}
        onChange={_onComplete}
        onContextChange={_onEntryChange}
        onContextManual={_onManual}
      />

      <Styled.Title>{task.name}</Styled.Title>

      {!!linkedItem && (
        <Styled.LinkedItemContainer>
          <Styled.LinkedItemPrefixContainer>
            {linkedItemColor ? (
              <Styled.Dot $color={linkedItemColor}></Styled.Dot>
            ) : (
              <Icon icon={Target} />
            )}
          </Styled.LinkedItemPrefixContainer>
          <Styled.LinkedItemTitle>{linkedItem.name}</Styled.LinkedItemTitle>
        </Styled.LinkedItemContainer>
      )}
    </Styled.Container>
  );
};
